import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getBugById, updateBug } from "../../services/bugService";
import { getProjects } from "../../services/projectService";

const EditBug = () => {
  const navigate = useNavigate();
  const { id: bugId } = useParams();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [priority, setPriority] = useState("low");
  const [status, setStatus] = useState("open");
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);

  const goBack = () => {
    navigate(`/bugs/${bugId}`);
  };

  useEffect(() => {
    async function loadData() {
      try {
        const [bugData, projectData] = await Promise.all([
          getBugById(bugId),
          getProjects(),
        ]);

        setTitle(bugData.title);
        setDescription(bugData.description);
        setProjectId(bugData.projectId);
        setPriority(bugData.priority);
        setStatus(bugData.status || "open");
        setProjects(projectData);
      } catch {
        setError("Failed to load bug details");
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [bugId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const formData = new FormData();
      formData.append("id", bugId);
      formData.append("title", title);
      formData.append("description", description);
      formData.append("project_id", projectId);
      formData.append("priority", priority);
      formData.append("status", status);
      formData.append("action", "update");

      for (let i = 0; i < files.length; i++) {
        formData.append("attachments[]", files[i]);
      }

      await updateBug(formData);
      goBack();
    } catch {
      setError("Failed to update bug");
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600">
        Loading bug details...
      </div>
    );
  }

  return (
    <div className="p-0 md:p-6 max-w-3xl mx-auto space-y-6">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">✏️ Edit Bug</h1>

        <button
          onClick={goBack}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
        >
          ← Back
        </button>
      </div>

      {/* FORM CARD */}
      <div className="bg-white p-6 rounded-xl shadow-sm border space-y-4">
        {error && (
          <p className="text-red-500 text-sm text-center">{error}</p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* TITLE */}
          <div>
            <label className="block font-medium mb-1">Bug Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring focus:ring-blue-200"
              required
            />
          </div>

          {/* DESCRIPTION */}
          <div>
            <label className="block font-medium mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="4"
              className="w-full px-3 py-2 border rounded-lg focus:ring focus:ring-blue-200"
              required
            />
          </div>

          {/* ATTACHMENTS */}
          <div>
            <label className="block font-medium mb-1">Attachments</label>
            <input
              type="file"
              multiple
              onChange={(e) => setFiles(e.target.files)}
              className="w-full px-3 py-2 border rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">
              Upload screenshots, logs, or PDFs
            </p>
          </div>

          {/* PROJECT */}
          <div>
            <label className="block font-medium mb-1">Project</label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              required
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          {/* PRIORITY */}
          <div>
            <label className="block font-medium mb-1">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          {/* STATUS */}
          <div>
            <label className="block font-medium mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="open">Open</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>

          {/* SUBMIT */}
          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 transition font-medium"
          >
            Update Bug
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditBug;
